'use client';

import { c as _c } from "react/compiler-runtime";
import dynamic from 'next/dynamic';
import type { RefCallback } from 'react';
import IconExperimence from '@/components/assets/icon_experimence.svg';
import IconGamepad from '@/components/assets/icon_gamepad.svg';
import IconPlay from '@/components/assets/icon_play.svg';
import OptimizedImage from '@/components/OptimizedImage';
import PremiumBadge from '@/components/PremiumBadge';
import { NEW_TAB_LINK_PROPS, WORLD_CARD_ADVERTISED_SRC_WIDTH } from '@/constants/card';
import { useWorldCardContext } from '@/context/ViverseWorldCardContext';
import { Link } from '@/i18n/navigation';
import { formatDuration } from '@/lib/format';
import { cn } from '@/lib/utils';
import roomDefaultImage from '@/public/images/room-default.png';
const HoverVideoPreview = dynamic(() => import('@/components/HoverVideoPreview'), {
  ssr: false
});
type ViverseWorldCardMediaProps = {
  isHovered: boolean;
  isHoverSupported: boolean;
  isDwellPlaying: boolean;
  cardRef: RefCallback<HTMLDivElement>;
};
export default function ViverseWorldCardMedia(t0) {
  const $ = _c(55);
  const {
    isHovered,
    isHoverSupported,
    isDwellPlaying,
    cardRef
  } = t0;
  const {
    targetUrl,
    imageAlt,
    cardType,
    resolvedImage,
    showPremiumTag,
    isClient,
    vodId,
    imageClassName,
    mediaClassName,
    imageSizes,
    priority,
    openLinksInNewTab,
    info: t1
  } = useWorldCardContext();
  const {
    is_live,
    vod_duration
  } = t1;
  const t2 = cardType === "videos" ? "relative aspect-320/180 w-full overflow-hidden rounded-3xl" : "relative aspect-430/242 w-full overflow-hidden rounded-3xl";
  let t3;
  if ($[0] !== mediaClassName || $[1] !== t2) {
    t3 = cn(t2, mediaClassName);
    $[0] = mediaClassName;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = openLinksInNewTab && NEW_TAB_LINK_PROPS;
  let t5;
  if ($[3] !== isDwellPlaying || $[4] !== isHovered || $[5] !== vodId) {
    t5 = (isHovered || isDwellPlaying) && vodId && <div className="fade-in animate-in absolute inset-0 z-10 duration-300"><HoverVideoPreview vodId={vodId} /></div>;
    $[3] = isDwellPlaying;
    $[4] = isHovered;
    $[5] = vodId;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = resolvedImage || roomDefaultImage.src;
  const t7 = isHoverSupported && "transition-all duration-500 group-hover:scale-115 group-hover:brightness-110";
  let t8;
  if ($[7] !== imageClassName || $[8] !== t7) {
    t8 = cn("select-none", t7, imageClassName);
    $[7] = imageClassName;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = imageSizes ?? "(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw";
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      objectFit: "cover"
    };
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== priority) {
    t11 = priority && {
      fetchPriority: "high",
      loading: "eager"
    };
    $[11] = priority;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== imageAlt || $[14] !== t11 || $[15] !== t6 || $[16] !== t8 || $[17] !== t9) {
    t12 = <OptimizedImage src={t6} overrideSrcWidth={WORLD_CARD_ADVERTISED_SRC_WIDTH} className={t8} fill={true} sizes={t9} alt={imageAlt} style={t10} draggable={false} {...t11} />;
    $[13] = imageAlt;
    $[14] = t11;
    $[15] = t6;
    $[16] = t8;
    $[17] = t9;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== cardRef || $[20] !== t12 || $[21] !== t5) {
    t13 = <div ref={cardRef} className="relative size-full">{t5}{t12}</div>;
    $[19] = cardRef;
    $[20] = t12;
    $[21] = t5;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t13 || $[24] !== t4 || $[25] !== targetUrl) {
    t14 = <Link href={targetUrl} prefetch={false} className="block size-full cursor-pointer" draggable={false} {...t4}>{t13}</Link>;
    $[23] = t13;
    $[24] = t4;
    $[25] = targetUrl;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== isClient || $[28] !== is_live || $[29] !== openLinksInNewTab || $[30] !== targetUrl) {
    t15 = is_live && isClient && <div className="absolute top-3 left-3"><Link href={targetUrl} prefetch={false} className="cursor-pointer" draggable={false} {...openLinksInNewTab && NEW_TAB_LINK_PROPS}><div className="caption flex items-center gap-2 rounded-full bg-[#00000099] px-2 text-white"><span className="bg-vive-live size-2 rounded-full" />Live</div></Link></div>;
    $[27] = isClient;
    $[28] = is_live;
    $[29] = openLinksInNewTab;
    $[30] = targetUrl;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== showPremiumTag) {
    t16 = showPremiumTag && <div className="absolute top-1.5 right-2"><PremiumBadge showLabel={true} /></div>;
    $[32] = showPremiumTag;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="z-raised pointer-events-none absolute inset-0 hidden bg-linear-to-b from-transparent from-50% to-[rgba(8,8,10,0.2)] opacity-0 transition-opacity duration-300 lg:block lg:group-hover:opacity-100" />;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== cardType || $[36] !== isDwellPlaying || $[37] !== isHovered) {
    t18 = cardType === "videos" && <div className={cn("pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-300 ease-out", isHovered || isDwellPlaying ? "opacity-0" : "opacity-100")}><div className="flex items-center justify-center rounded-full bg-white p-4 drop-shadow-[0px_0px_6px_rgba(0,0,0,0.55)]"><IconPlay className="size-[15px] fill-black" /></div></div>;
    $[35] = cardType;
    $[36] = isDwellPlaying;
    $[37] = isHovered;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== cardType || $[40] !== vod_duration) {
    t19 = vod_duration !== undefined && cardType === "videos" && <div className="pointer-events-none absolute right-2 bottom-2 rounded-full bg-[#121216] px-2 py-0.5 text-[12px] font-medium text-[#d5e2f2] backdrop-blur-md">{formatDuration(vod_duration)}</div>;
    $[39] = cardType;
    $[40] = vod_duration;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== cardType) {
    t20 = cardType === "experiences" && <div className="absolute right-0 bottom-0 rounded-tl-2xl bg-linear-to-b from-[#ff7cff] to-[#903bff] p-1.5"><IconExperimence className="size-6 fill-white" /></div>;
    $[42] = cardType;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== cardType) {
    t21 = cardType === "games" && <div className="absolute right-0 bottom-0 rounded-tl-2xl bg-linear-to-b from-[#33c2e9] to-[#3478f6] p-1.5"><IconGamepad className="size-6 fill-white" /></div>;
    $[44] = cardType;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t14 || $[47] !== t15 || $[48] !== t16 || $[49] !== t18 || $[50] !== t19 || $[51] !== t20 || $[52] !== t21 || $[53] !== t3) {
    t22 = <div className={t3}>{t14}{t15}{t16}{t17}{t18}{t19}{t20}{t21}</div>;
    $[46] = t14;
    $[47] = t15;
    $[48] = t16;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
    $[52] = t21;
    $[53] = t3;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  return t22;
}